import React, { useEffect } from 'react';
import { ScrollView, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useQuery } from '@tanstack/react-query';
import { CompetitorCircuitRole, MyMissionClient } from '../../api/api.g';
import { CircuitSurveyContext, circuitSurveyReturnMessage, getCircuitSubmissionState } from '../../helpers/competitorCircuitSurvey';
import { useAppDispatch } from '../../hooks/store-hook';
import { missionsActions } from '../../store/missionsSlice';
import { StringHelper } from '../../helpers/stringHelper';
import useApi from '../../hooks/api-hook';
import useTranslation from '../../hooks/translation-hook';
import useCustomTheme from '../../hooks/theme-hook';
import CustomButton from '../common/CustomButton';
import Label, { LabelType } from '../common/Label';
import Loading from '../common/Loading';

type Props = { context: CircuitSurveyContext; isWebview?: boolean };

const CompetitorCircuitSurveyCompleted = ({ context, isWebview }: Props) => {
  const t = useTranslation();
  const theme = useCustomTheme();
  const navigation = useNavigation();
  const dispatch = useAppDispatch();
  const api = useApi(MyMissionClient);
  const circuit = useQuery({
    queryKey: ['competitor-circuit', context.circuitId],
    queryFn: () => api.getCompetitorCircuit(context.circuitId, api.tokenSource?.token),
    staleTime: 0,
    refetchOnMount: 'always',
    retry: 1,
    refetchInterval: (data) => getCircuitSubmissionState(data).waitingForComparison ? 2000 : false,
  });
  const state = getCircuitSubmissionState(circuit.data);
  const messageKey = circuit.isError ? 'competitor-circuit-progress-unavailable'
    : state.cancelled ? 'Cancelled'
      : state.approved ? 'competitor-circuit-approved'
      : state.allSubmitted ? 'competitor-circuit-awaiting-validation'
      : state.comparisonAvailable ? 'competitor-circuit-comparison-ready'
        : state.waitingForComparison ? 'competitor-circuit-comparison-updating' : 'competitor-circuit-choose-visit';

  useEffect(() => {
    // A completed visit must never select a hidden competitor child in My Missions.
    dispatch(missionsActions.setMissionAcceptedId(context.mainMissionAssignationId));
  }, [context.mainMissionAssignationId, dispatch]);

  const returnToCircuit = () => {
    if (isWebview && typeof window !== 'undefined' && (window as any).ReactNativeWebView?.postMessage) {
      (window as any).ReactNativeWebView.postMessage(circuitSurveyReturnMessage(context));
      return;
    }
    dispatch(missionsActions.setReloadMissionListSeed(StringHelper.generateRandomString()));
    navigation.navigate({ name: 'mymissions', params: { id: context.mainMissionAssignationId } } as never);
  };

  return <ScrollView contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', padding: 24, backgroundColor: theme.background.primary }}>
    <View style={{ width: '100%', maxWidth: 520, alignSelf: 'center', gap: 20 }}>
      <Label variant={LabelType.titleLargeNoCapsPrimary} style={{ textAlign: 'center' }}>{t('competitor-circuit-questionnaire-submitted')}</Label>
      <Label style={{ textAlign: 'center', fontWeight: '600' }}>
        {context.role === CompetitorCircuitRole.FollowUp ? t('competitor-circuit-follow-up') : context.visitName}
      </Label>
      {circuit.isFetching ? <Loading /> : <>
        {!!circuit.data && <Label style={{ textAlign: 'center' }}>{t('competitor-circuit-progress', {
          completed: circuit.data.completedSteps ?? 0, total: circuit.data.totalSteps ?? 0,
        })}</Label>}
        <Label style={{ textAlign: 'center' }}>{t(messageKey)}</Label>
      </>}
      <CustomButton title={t('competitor-circuit-return')} onPress={returnToCircuit} />
      {state.allSubmitted && !isWebview && <CustomButton secondary title={t('missions-available')} onPress={() => navigation.navigate('map' as never)} />}
    </View>
  </ScrollView>;
};

export default CompetitorCircuitSurveyCompleted;
